import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import RichTextEditor from '@/components/RichTextEditor';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, X } from 'lucide-react';
import {
  GENDER_OPTIONS, AGE_GROUP_OPTIONS, COUNTRY_OPTIONS,
  REGION_OPTIONS, YEARS_SUSTAINABLE_LOGISTICS_OPTIONS, SECTOR_OPTIONS, FORUM_TOPIC_OPTIONS
} from './profileConstants';
import useProfileOptions from '@/hooks/useProfileOptions';

const BACKEND_URL = '';

function CompanySelector({ companyId, companyName, onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const token = sessionStorage.getItem('session_token');

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/companies/search?q=${encodeURIComponent(query)}`, {
          credentials: 'include', headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) { setResults(await res.json()); setOpen(true); }
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (companyId && companyName) {
    return (
      <div className="flex items-center gap-2 mt-1.5 p-2.5 bg-brand-navy/5 rounded-lg border border-brand-navy/10">
        <Building2 className="h-4 w-4 text-brand-navy flex-shrink-0" />
        <span className="text-sm font-medium text-slate-700 flex-1 truncate">{companyName}</span>
        <button onClick={() => onSelect(null, null)} className="text-slate-400 hover:text-red-500 transition-colors" data-testid="clear-company-btn">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <Input
        data-testid="company-search-input"
        placeholder="Search for your company..."
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => { if (results.length) setOpen(true); }}
        className="mt-1.5"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="company-search-results">
          {results.map(c => (
            <button key={c.company_id} data-testid={`company-option-${c.company_id}`}
              onClick={() => { onSelect(c.company_id, c.name); setQuery(''); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors">
              <Building2 className="h-4 w-4 text-brand-navy/40 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-700 truncate">{c.name}</p>
                {c.sector && <p className="text-[11px] text-slate-400">{c.sector}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProfileTab({ user, profile, set, handleSave, isNewUser, companyId, companyName, onCompanyChange }) {
  const token = sessionStorage.getItem('session_token');
  const opts = useProfileOptions();
  const STAKEHOLDER_TYPE_OPTIONS = opts.stakeholder_type;
  const SELF_DESCRIPTION_OPTIONS = opts.self_description;

  // Legacy values: "UK & Ireland" was removed from the region list because
  // UK + Ireland are part of Europe for this community. Normalise any
  // existing profile to "Europe" on load so saving doesn't write the
  // dead value back to the database.
  useEffect(() => {
    if (profile.region === 'UK & Ireland') set('region', 'Europe');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.region]);

  return (
    <>
      <Card className="p-8 border border-slate-200 mb-6">
        <h2 className="text-lg font-bold font-heading mb-1">About You</h2>
        <p className="text-sm text-slate-500 mb-6">Tell the community who you are</p>
        <div className="space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
              <Input id="name" data-testid="profile-name-input" value={profile.name}
                onChange={(e) => set('name', e.target.value)} className="mt-1.5" required />
            </div>
            <div>
              <Label htmlFor="role">Job Title <span className="text-red-500">*</span></Label>
              <Input id="role" data-testid="profile-role-input" placeholder="e.g., Head of Sustainability"
                value={profile.role} onChange={(e) => set('role', e.target.value)} className="mt-1.5" required />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="organisation">Organisation <span className="text-red-500">*</span></Label>
              <Input id="organisation" data-testid="profile-organisation-input" placeholder="Company name"
                value={profile.organisation} onChange={(e) => set('organisation', e.target.value)} className="mt-1.5" required />
              <p className="text-[11px] text-slate-500 mt-1" data-testid="organisation-helper">
                Please add your company name. If it is already registered it will automatically link below; otherwise please add manually.
              </p>
            </div>
            <div>
              <Label>Company Profile</Label>
              <CompanySelector companyId={companyId} companyName={companyName} onSelect={onCompanyChange} />
              <p className="text-[11px] text-slate-400 mt-1">Link to your company's profile in the directory — only works if your company is already listed.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label>Stakeholder Type <span className="text-red-500">*</span></Label>
              <Select value={profile.background} onValueChange={(v) => set('background', v)}>
                <SelectTrigger data-testid="profile-background-select" className="mt-1.5"><SelectValue placeholder="Select stakeholder type" /></SelectTrigger>
                <SelectContent>
                  {STAKEHOLDER_TYPE_OPTIONS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label>Gender</Label>
              <Select value={profile.gender} onValueChange={(v) => set('gender', v)}>
                <SelectTrigger data-testid="profile-gender-select" className="mt-1.5"><SelectValue placeholder="Select gender" /></SelectTrigger>
                <SelectContent>{GENDER_OPTIONS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Age Group</Label>
              <Select value={profile.age_group} onValueChange={(v) => set('age_group', v)}>
                <SelectTrigger data-testid="profile-age-group-select" className="mt-1.5"><SelectValue placeholder="Select age group" /></SelectTrigger>
                <SelectContent>{AGE_GROUP_OPTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label>Country <span className="text-red-500">*</span></Label>
              <Select value={profile.country} onValueChange={(v) => set('country', v)}>
                <SelectTrigger data-testid="profile-country-select" className="mt-1.5"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent>{COUNTRY_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="location">City <span className="text-red-500">*</span></Label>
              <Input id="location" data-testid="profile-location-input" placeholder="e.g., Westcliff-on-Sea"
                value={profile.location} onChange={(e) => set('location', e.target.value)} className="mt-1.5" required />
              <p className="text-[11px] text-slate-400 mt-1">Autofilled if UK postcode added below — please edit if required.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="postcode">Postcode / Zip Code</Label>
              <Input id="postcode" data-testid="profile-postcode-input" placeholder="e.g., LS18 5SE"
                value={profile.postcode}
                onChange={(e) => set('postcode', e.target.value)}
                onBlur={async (e) => {
                  const pc = e.target.value.trim();
                  if (!pc) return;
                  try {
                    const res = await fetch(`/api/postcode/lookup?postcode=${encodeURIComponent(pc)}`, { credentials: 'include' });
                    if (res.ok) {
                      const d = await res.json();
                      if (d.city) {
                        // Always overwrite — postcode is the source of truth for City
                        set('location', d.city);
                        if (d.region && !profile.region) set('region', d.region);
                      }
                    }
                  } catch { /* silent — user can still type city manually */ }
                }}
                className="mt-1.5" />
              <p className="text-[11px] text-slate-400 mt-1">UK postcodes (with the gap, e.g. <span className="font-mono">LS18 5SE</span>) auto-fill your City.</p>
            </div>
            <div>
              <Label>Region</Label>
              <Select value={profile.region} onValueChange={(v) => set('region', v)}>
                <SelectTrigger data-testid="profile-region-select" className="mt-1.5"><SelectValue placeholder="Select region" /></SelectTrigger>
                <SelectContent>
                  {REGION_OPTIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label>Years of Sustainable Logistics Experience</Label>
              <Select value={profile.years_experience} onValueChange={(v) => set('years_experience', v)}>
                <SelectTrigger data-testid="profile-years-select" className="mt-1.5"><SelectValue placeholder="Select experience" /></SelectTrigger>
                <SelectContent>
                  {YEARS_SUSTAINABLE_LOGISTICS_OPTIONS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sector <span className="text-red-500">*</span></Label>
              <Select value={profile.sector} onValueChange={(v) => set('sector', v)}>
                <SelectTrigger data-testid="profile-sector-select" className="mt-1.5"><SelectValue placeholder="Select sector" /></SelectTrigger>
                <SelectContent>
                  {SECTOR_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="self_desc">How Would You Describe Yourself <span className="text-red-500">*</span></Label>
            <p className="text-xs text-slate-400 mt-0.5 mb-2">Pick up to 5 that resonate with you</p>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="self-description-picker">
              {SELF_DESCRIPTION_OPTIONS.map(opt => {
                const list = Array.isArray(profile.self_descriptions) ? profile.self_descriptions
                  : (profile.self_description ? [profile.self_description] : []);
                const selected = list.includes(opt);
                const atMax = list.length >= 5 && !selected;
                return (
                  <button
                    key={opt}
                    type="button"
                    disabled={atMax}
                    data-testid={`self-desc-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => {
                      let next;
                      if (selected) next = list.filter(x => x !== opt);
                      else if (list.length < 5) next = [...list, opt];
                      else return;
                      set('self_descriptions', next);
                      // Keep legacy single-string field in sync (first selection) for backward compat
                      set('self_description', next[0] || '');
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                      selected
                        ? 'bg-brand-navy text-white border-brand-navy'
                        : atMax
                          ? 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-brand-blue'
                    }`}>{opt}</button>
                );
              })}
            </div>
          </div>
          <div>
            <Label htmlFor="bio">About Me <span className="text-red-500">*</span></Label>
            <RichTextEditor value={profile.bio || ''} onChange={v => set('bio', v)}
              placeholder="Tell the community about your background — use bold, colour, links…"
              testId="profile-bio-textarea" minHeight="7rem" />
          </div>
        </div>
      </Card>

      <Card className="p-8 border border-slate-200 mb-6">
        <h2 className="text-lg font-bold font-heading mb-1">Contact Details</h2>
        <p className="text-sm text-slate-500 mb-6">How people can reach you</p>
        <div className="space-y-5">
          <div>
            <Label>Email</Label>
            <Input value={user?.email || ''} disabled className="mt-1.5 bg-slate-50 text-slate-500" />
            <p className="text-xs text-slate-400 mt-1">Email cannot be changed</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="mobile">Mobile Number <span className="text-xs text-slate-400 font-normal">(optional)</span></Label>
              <Input id="mobile" data-testid="profile-mobile-input" placeholder="+44 7xxx xxx xxx" type="tel"
                value={profile.mobile_number} onChange={(e) => set('mobile_number', e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="linkedin">LinkedIn URL</Label>
              <Input id="linkedin" data-testid="profile-linkedin-input" placeholder="https://linkedin.com/in/yourprofile"
                value={profile.linkedin_url} onChange={(e) => set('linkedin_url', e.target.value)} className="mt-1.5" />
            </div>
          </div>
        </div>
      </Card>

      {/* Forum Topics of Interest */}
      <Card className="p-8 border border-slate-200 mb-6">
        <h2 className="text-lg font-bold font-heading mb-1">Forum Topics of Interest</h2>
        <p className="text-sm text-slate-500 mb-4">Select the forum topics you're interested in — posts on these topics will be prioritised in your feed</p>
        <div className="flex flex-wrap gap-2" data-testid="forum-topics-of-interest">
          {FORUM_TOPIC_OPTIONS.map(topic => {
            const selected = (profile.forum_topics_of_interest || []).includes(topic);
            return (
              <button key={topic} type="button" data-testid={`forum-topic-${topic.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => {
                  const current = profile.forum_topics_of_interest || [];
                  const updated = selected ? current.filter(t => t !== topic) : [...current, topic];
                  set('forum_topics_of_interest', updated);
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  selected
                    ? 'bg-brand-navy text-white border-brand-navy'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-brand-navy/40 hover:bg-brand-navy/5'
                }`}>
                {topic}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Behavioural Profile */}
      <Card className="p-8 border border-slate-200 mb-6">
        <h2 className="text-lg font-bold font-heading mb-1">Behavioural Profile</h2>
        <p className="text-sm text-slate-500 mb-6">These questions help us understand your working style for better team matching and collaboration</p>

        <div className="space-y-6">
          <div data-testid="behavioural-decisions">
            <p className="text-sm font-medium text-slate-700 mb-3">When making decisions in complex projects, I typically: <span className="text-red-500">*</span></p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-slate-50" data-testid="decisions-option-a"
                style={{ borderColor: profile.behavioural_decisions === 'data_analysis' ? '#1e3a5f' : undefined, backgroundColor: profile.behavioural_decisions === 'data_analysis' ? '#f8fafc' : undefined }}>
                <input type="radio" name="behavioural_decisions" value="data_analysis" checked={profile.behavioural_decisions === 'data_analysis'}
                  onChange={() => set('behavioural_decisions', 'data_analysis')}
                  className="h-4 w-4 text-brand-navy focus:ring-brand-navy" />
                <span className="text-sm text-slate-600">Prioritise data and objective analysis</span>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-slate-50" data-testid="decisions-option-b"
                style={{ borderColor: profile.behavioural_decisions === 'stakeholder_impact' ? '#1e3a5f' : undefined, backgroundColor: profile.behavioural_decisions === 'stakeholder_impact' ? '#f8fafc' : undefined }}>
                <input type="radio" name="behavioural_decisions" value="stakeholder_impact" checked={profile.behavioural_decisions === 'stakeholder_impact'}
                  onChange={() => set('behavioural_decisions', 'stakeholder_impact')}
                  className="h-4 w-4 text-brand-navy focus:ring-brand-navy" />
                <span className="text-sm text-slate-600">Consider stakeholder impact and relationships</span>
              </label>
            </div>
          </div>

          <div data-testid="behavioural-processing">
            <p className="text-sm font-medium text-slate-700 mb-3">When processing information I prefer to: <span className="text-red-500">*</span></p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-slate-50" data-testid="processing-option-a"
                style={{ borderColor: profile.behavioural_processing === 'practical_proven' ? '#1e3a5f' : undefined, backgroundColor: profile.behavioural_processing === 'practical_proven' ? '#f8fafc' : undefined }}>
                <input type="radio" name="behavioural_processing" value="practical_proven" checked={profile.behavioural_processing === 'practical_proven'}
                  onChange={() => set('behavioural_processing', 'practical_proven')}
                  className="h-4 w-4 text-brand-navy focus:ring-brand-navy" />
                <span className="text-sm text-slate-600">Focus on practical details and proven approaches</span>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-slate-50" data-testid="processing-option-b"
                style={{ borderColor: profile.behavioural_processing === 'new_ideas' ? '#1e3a5f' : undefined, backgroundColor: profile.behavioural_processing === 'new_ideas' ? '#f8fafc' : undefined }}>
                <input type="radio" name="behavioural_processing" value="new_ideas" checked={profile.behavioural_processing === 'new_ideas'}
                  onChange={() => set('behavioural_processing', 'new_ideas')}
                  className="h-4 w-4 text-brand-navy focus:ring-brand-navy" />
                <span className="text-sm text-slate-600">Explore new ideas and future possibilities</span>
              </label>
            </div>
          </div>

          <div data-testid="behavioural-working">
            <p className="text-sm font-medium text-slate-700 mb-3">When working on a project, I: <span className="text-red-500">*</span></p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-slate-50" data-testid="working-option-a"
                style={{ borderColor: profile.behavioural_working === 'structured_timelines' ? '#1e3a5f' : undefined, backgroundColor: profile.behavioural_working === 'structured_timelines' ? '#f8fafc' : undefined }}>
                <input type="radio" name="behavioural_working" value="structured_timelines" checked={profile.behavioural_working === 'structured_timelines'}
                  onChange={() => set('behavioural_working', 'structured_timelines')}
                  className="h-4 w-4 text-brand-navy focus:ring-brand-navy" />
                <span className="text-sm text-slate-600">Prefer structure, timelines, and clear decisions</span>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-slate-50" data-testid="working-option-b"
                style={{ borderColor: profile.behavioural_working === 'flexible_adaptive' ? '#1e3a5f' : undefined, backgroundColor: profile.behavioural_working === 'flexible_adaptive' ? '#f8fafc' : undefined }}>
                <input type="radio" name="behavioural_working" value="flexible_adaptive" checked={profile.behavioural_working === 'flexible_adaptive'}
                  onChange={() => set('behavioural_working', 'flexible_adaptive')}
                  className="h-4 w-4 text-brand-navy focus:ring-brand-navy" />
                <span className="text-sm text-slate-600">Stay flexible and adapt as new information emerges</span>
              </label>
            </div>
          </div>

          <div data-testid="behavioural-collaboration">
            <p className="text-sm font-medium text-slate-700 mb-3">In collaborative environments, I: <span className="text-red-500">*</span></p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-slate-50" data-testid="collaboration-option-a"
                style={{ borderColor: profile.behavioural_collaboration === 'think_aloud' ? '#1e3a5f' : undefined, backgroundColor: profile.behavioural_collaboration === 'think_aloud' ? '#f8fafc' : undefined }}>
                <input type="radio" name="behavioural_collaboration" value="think_aloud" checked={profile.behavioural_collaboration === 'think_aloud'}
                  onChange={() => set('behavioural_collaboration', 'think_aloud')}
                  className="h-4 w-4 text-brand-navy focus:ring-brand-navy" />
                <span className="text-sm text-slate-600">Think out loud and engage actively in discussions</span>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-slate-50" data-testid="collaboration-option-b"
                style={{ borderColor: profile.behavioural_collaboration === 'reflect_first' ? '#1e3a5f' : undefined, backgroundColor: profile.behavioural_collaboration === 'reflect_first' ? '#f8fafc' : undefined }}>
                <input type="radio" name="behavioural_collaboration" value="reflect_first" checked={profile.behavioural_collaboration === 'reflect_first'}
                  onChange={() => set('behavioural_collaboration', 'reflect_first')}
                  className="h-4 w-4 text-brand-navy focus:ring-brand-navy" />
                <span className="text-sm text-slate-600">Reflect first and contribute thoughtfully</span>
              </label>
            </div>
          </div>
        </div>
      </Card>

      {/* BriteLab Consent */}
      <Card className="p-8 border border-slate-200 mb-6">
        <h2 className="text-lg font-bold font-heading mb-1">BriteLab Participation</h2>
        <p className="text-sm text-slate-500 mb-4">To use the BriteLab function please tick below</p>
        <label className="flex items-start gap-3 cursor-pointer group" data-testid="britelab-consent-label">
          <input type="checkbox" checked={!!profile.britelab_consent}
            onChange={(e) => set('britelab_consent', e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-navy focus:ring-brand-navy"
            data-testid="britelab-consent-checkbox" />
          <span className="text-sm text-slate-600 leading-relaxed">
            You agree that your data can be used for research purposes to consider the level of impact of the BriteLab functionality. Personal contact details would not be shared.
          </span>
        </label>
      </Card>

      <Button data-testid="save-profile-button" onClick={handleSave}
        className="w-full h-12 text-base bg-gradient-to-r from-brand-navy to-brand-sage hover:from-brand-dark hover:to-brand-sage text-white">
        {isNewUser ? 'Complete Profile & Continue' : 'Save Profile'}
      </Button>
    </>
  );
}
